import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TbChevronRight, TbLink, TbPlus, TbSearch, TbSortAscending, TbSortDescending } from 'react-icons/tb';
import { HiUserGroup } from 'react-icons/hi2';
import { RootState } from '../../../redux/reducers';
import { store } from '../../../redux/store';
import { setConnections } from '../../../redux/slices/connectionsSlice';
import ApiImage from '../../components/misc/ApiImage';
import Community from '../../../types/community';
import communityService from '../../../services/community/communityService';
import tagService from '../../../services/community/tagService';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import CommunityTagDisplay from '../../components/misc/CommunityTagDisplay';
import FilterOnCommunityTags from '../../components/dropdown/FilterOnCommunityTags';
import ConnectExistingCommunityModal from '../../components/modals/ConnectExistingCommunityModal';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';

type SortField = 'name' | 'type' | 'members' | 'tags';
type SortDirection = 'asc' | 'desc';

interface SortableHeaderProps {
  field: SortField;
  label: string;
  className?: string;
  active?: boolean;
  direction?: SortDirection;
  onSort: (field: SortField) => void;
}

function SortableHeader({ field, label, className, active = false, direction = 'asc', onSort }: SortableHeaderProps) {
  const Icon = direction === 'asc' ? TbSortAscending : TbSortDescending;
  return (
    <th className={`cursor-pointer select-none ${className ?? ''}`} onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <Icon className="h-4 w-4 text-primary-600" />}
      </span>
    </th>
  );
}

export default function CommunitiesPage(): JSX.Element {
  const { t } = useTranslation();
  const businesses = useSelector((state: RootState) => state.application.businesses);
  const selectedBusinessId = useSelector((state: RootState) => state.application.selectedBusiness);
  const navigate = useNavigate();
  const [businessCommunities, setBusinessCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTags, setFilteredTags] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [connectModalOpen, setConnectModalOpen] = useState(false);

  const communityTags = useSelector((state: RootState) => state.communityTags);

  const handleTagFilterClick = (tagId: string): void => {
    setFilteredTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  const fetchCommunities = useCallback(async () => {
    if (!selectedBusinessId) return;
    try {
      setLoading(true);
      const data = await communityService.fetchCommunitiesForBusiness(selectedBusinessId);
      setBusinessCommunities(data);
    } catch (error) {
      console.error('Failed to fetch business communities:', error);
    }
    setLoading(false);
  }, [selectedBusinessId]);

  useEffect(() => {
    fetchCommunities();
    tagService.fetchCommunityTags();
    store.dispatch(setConnections([]));
  }, [fetchCommunities]);

  const selectedBusiness = businesses?.find((b) => b.businessId === selectedBusinessId);
  const mainCommunityId = selectedBusiness?.community?.id;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedCommunities = useMemo(() => {
    const base = businessCommunities.filter((c) => c.id !== mainCommunityId);

    const searched = searchQuery.trim()
      ? base.filter((c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.type.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : base;

    const tagFiltered =
      filteredTags.length > 0
        ? searched.filter((c) => filteredTags.some((tagId) => c.tags?.includes(Number(tagId))))
        : searched;

    return [...tagFiltered].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'type') cmp = a.type.localeCompare(b.type);
      else if (sortField === 'members') cmp = (a.memberCount ?? 0) - (b.memberCount ?? 0);
      else if (sortField === 'tags') cmp = (a.tags?.length ?? 0) - (b.tags?.length ?? 0);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [businessCommunities, mainCommunityId, searchQuery, filteredTags, sortField, sortDirection]);

  const linkedCommunityIds = useMemo(
    () => businessCommunities.map((c) => c.id),
    [businessCommunities],
  );

  return (
    <>
      <PageHeader
        title={t('page.communities.title')}
        subtitle={t('page.communities.subtitle')}
        secondaryButton={
          <Button
            variant="primary-outline"
            onClick={() => setConnectModalOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap">
            <TbLink className="h-4 w-4" />
            {t('page.communities.connect')}
          </Button>
        }
        primaryButton={
          <PrimaryButton
            text={t('page.communities.new')}
            icon={TbPlus}
            onClick={() => navigate('/onboarding?mode=community')}
          />
        }
      />

      {selectedBusinessId && (
        <ConnectExistingCommunityModal
          open={connectModalOpen}
          setOpen={setConnectModalOpen}
          businessId={selectedBusinessId}
          mainCommunityId={mainCommunityId}
          linkedCommunityIds={linkedCommunityIds}
          onCommunityConnected={fetchCommunities}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">{t('general.loading')}</p>
        </div>
      ) : businessCommunities.filter((c) => c.id !== mainCommunityId).length === 0 ? (
        <EmptyScreenView
          title={t('page.communities.emptyTitle')}
          subtitle={t('page.communities.emptyText')}
          primaryButton={
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                variant="primary-outline"
                onClick={() => setConnectModalOpen(true)}
                className="flex items-center gap-2">
                <TbLink className="h-4 w-4" />
                {t('page.communities.connect')}
              </Button>
              <PrimaryButton
                text={t('page.communities.new')}
                icon={TbPlus}
                onClick={() => navigate('/onboarding?mode=community')}
              />
            </div>
          }
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('general.search')}
                className="w-full rounded-[8px] border border-secondary-200 bg-surface py-2 pl-9 pr-3 text-sm outline-none ring-primary-300 transition focus:border-primary-400 focus:ring-2"
              />
            </div>
          </div>

          <BaseTable>
            <BaseTableHeader>
              <BaseTableHeaderRow>
                <SortableHeader
                  field="name"
                  label={t('page.communities.table.name')}
                  className="text-left"
                  active={sortField === 'name'}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="members"
                  label={t('page.communities.table.members')}
                  className="text-left"
                  active={sortField === 'members'}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <th
                  className="cursor-pointer select-none text-left"
                  onClick={() => handleSort('tags')}>
                  <span className="inline-flex items-center gap-1">
                    {t('page.communities.table.tags')}
                    {sortField === 'tags' && (
                      sortDirection === 'asc'
                        ? <TbSortAscending className="h-4 w-4 text-primary-600" />
                        : <TbSortDescending className="h-4 w-4 text-primary-600" />
                    )}
                    {communityTags.length > 0 && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <FilterOnCommunityTags
                          tags={communityTags}
                          onTagClick={handleTagFilterClick}
                          filterTags={filteredTags}
                        />
                      </span>
                    )}
                  </span>
                </th>
                <th className="text-right" />
              </BaseTableHeaderRow>
            </BaseTableHeader>
            <BaseTableBody>
              <BaseTableSpacerRow />
              {filteredAndSortedCommunities.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-10 pl-8 text-sm text-gray-400">
                    {t('general.noResults')}
                  </td>
                </tr>
              ) : (
                filteredAndSortedCommunities.map((community) => (
                  <CommunityRow key={community.id} community={community} onTagsChanged={fetchCommunities} />
                ))
              )}
            </BaseTableBody>
          </BaseTable>
        </>
      )}
    </>
  );
}

interface CommunityRowProps {
  community: Community;
  onTagsChanged: () => void;
}

function CommunityRow({ community, onTagsChanged }: CommunityRowProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleView = () => {
    navigate(`/my-communities/manage-community?id=${community.id}`, { state: { community } });
  };

  return (
    <>
      <BaseTableDataRow className="group transition-colors hover:bg-secondary-50">
        <td className="py-3.5">
          <div className="flex items-center gap-3">
            {community.picture ? (
              <ApiImage
                alt={community.name}
                path={community.picture}
                className="h-10 w-10 flex-shrink-0 rounded-lg object-cove"
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-secondary-200 rounded-lg">
                <HiUserGroup className="h-5 w-5 text-primary-700" />
              </div>
            )}
            <span className="font-semibold text-gray-900">{community.name}</span>
          </div>
        </td>
        <td className="py-3.5">
          {community.memberCount != null ? (
            <span className="inline-flex items-center gap-1 font-medium text-gray-800">
              <HiUserGroup className="h-4 w-4 text-primary-900" />
              {community.memberCount}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>
        <td className="py-3.5">
          <CommunityTagDisplay community={community} onTagsChanged={onTagsChanged} />
        </td>
        <td className="py-3.5 pr-4">
          <div className="flex justify-end">
            <Button variant="secondary" onClick={handleView}>
              {t('page.communities.actions.open')}
              <TbChevronRight className="h-3 w-3 text-primary-900" />
            </Button>
          </div>
        </td>
      </BaseTableDataRow>
    </>
  );
}

